// v2/shared/components/ErrorBoundary.tsx — React error boundary for the v2 route tree

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@components/shadcn-ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches runtime errors in the v2 component tree and displays a recovery UI
 * instead of a blank screen. Provides "Try Again" (resets error state) and
 * "Go Home" (navigates to dashboard) actions.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console for debugging; a production logger can be wired here later
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/v2';
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="mb-4 text-[var(--color-error-500)]">
          <AlertTriangle className="h-12 w-12" />
        </div>

        <h2
          className="text-xl font-semibold mb-2"
          style={{ color: 'var(--color-neutral-800)' }}
        >
          Something went wrong
        </h2>

        <p
          className="text-sm max-w-md mb-6"
          style={{ color: 'var(--color-neutral-500)' }}
        >
          An unexpected error occurred. You can try again or return to the dashboard.
        </p>

        {this.state.error && (
          <pre
            className="text-xs max-w-lg mb-6 p-3 rounded-md overflow-auto text-left"
            style={{
              backgroundColor: 'var(--color-error-50)',
              color: 'var(--color-error-700)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            {this.state.error.message}
          </pre>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={this.handleGoHome}
            className="min-h-[44px] gap-2"
          >
            <Home className="h-4 w-4" />
            Go Home
          </Button>
          <Button
            onClick={this.handleRetry}
            className="min-h-[44px] gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }
}
